import { Box, Divider, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { currentTeam } from '../../recoil/atoms/currentTeam';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import CallReview from '../components/Snapshot/CallReview/CallReview';
import AgentCallMetricsBar, {
  AgentBarSeries,
} from '../components/Snapshot/Metrics/AgentCallMetricsBar';
import CallMetrics from '../components/Snapshot/Metrics/CallMetrics';
import SnapshotDateDisplay from '../components/Snapshot/SnapshotDateDisplay';
import SnapshotDateTabs from '../components/Snapshot/SnapshotTabs/SnapshotDateTabs';
import SnapshotTabsBase, {
  ISnapshotTab,
} from '../components/Snapshot/SnapshotTabs/SnapshotTabsBase';
import SnapshotTitle from '../components/Snapshot/SnapshotTitle';
import { getDateRanges } from '../components/Snapshot/utils/getDateRanges';
import { DashboardTourExtraSpotlights } from '../pages/dashboardTour/DashboardTourExtraSpotlights';

const useStyles = makeStyles((theme: Theme) => ({
  chartTitle: {
    color: '#333333',
    fontSize: '24px',
    fill: '#333333',
    margin: theme.spacing(3),
  },
  divider: {
    margin: '0px 36px 48px 36px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? 'solid 1px rgb(194,192,195)'
        : '1px solid #ccc',
  },
}));

const getCallReviewTabs = (
  hasCallTranscriptionProduct: boolean
): ISnapshotTab[] =>
  hasCallTranscriptionProduct
    ? [
        {
          label: 'Positive Calls',
        },
        {
          label: 'Negative Calls',
        },
      ]
    : [
        {
          label: 'Outbound',
        },
        {
          label: 'Inbound',
        },
      ];

export default function AgentSnapshot() {
  const [callValue, setCallValue] = React.useState(0);
  const [dateInterval, setDateInterval] = useState<string>('day');
  const [team, _] = useRecoilState(currentTeam);
  const [starting, setStarting] = useState(moment());
  const [ending, setEnding] = useState(moment());
  const { id } = useParams<{ id: string }>();

  const classes = useStyles();
  const dateIntervals = ['day', 'week', 'month', 'quarter'];
  const setDates = (localStart: any, localEnd: any, index: number) => {
    setStarting(localStart);
    setEnding(localEnd);
    setDateInterval(dateIntervals[index]);
  };
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [
    agentCallMetricsCached,
    agentCallMetricsLoadable,
    setRefetchToggleForAgentCallMetrics,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_CALL_METRICS,
      payloadJsonString: JSON.stringify({
        businessid: userAtomContents?.businessid,
        interval: dateInterval,
        id: id,
        type: 'user',
        source: 'agent-call-metrics',
      }),
    },
    (loadable, cached) =>
      loadable?.contents?.response?.data?.charts ?? cached.current
  );

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const hasCallTranscriptionProduct = useMemo(() => {
    return (businessUsersLoadable?.contents?.response?.data?.result ?? []).some(
      (user: any) => user.userid == id && user.hasPdCallTranscription === 1
    );
  }, [businessUsersLoadable, id]);

  const callReviewTabs = getCallReviewTabs(hasCallTranscriptionProduct);

  useEffect(() => {
    setRefetchToggleForAgentCallMetrics({});
  }, []);

  const agentSeries = useMemo<AgentBarSeries>(
    () =>
      agentCallMetricsLoadable.state === 'hasValue'
        ? {
            calls:
              agentCallMetricsCached?.current?.calls?.outbound?.total +
                agentCallMetricsCached?.current?.calls?.inbound?.total ?? 0,
            connections:
              agentCallMetricsCached?.current?.connections?.total ?? 0,
            outbound:
              agentCallMetricsCached?.current?.calls?.outbound?.total ?? 0,
            inbound:
              agentCallMetricsCached?.current?.calls?.inbound?.total ?? 0,
            missedCalls:
              agentCallMetricsCached?.current?.calls?.inbound?.missed?.total ??
              0,
            voicemails: agentCallMetricsCached?.current?.voicemails ?? 0,
            sms: agentCallMetricsCached?.current?.sms ?? 0,
          }
        : {
            calls: 0,
            connections: 0,
            outbound: 0,
            inbound: 0,
            missedCalls: 0,
            voicemails: 0,
            sms: 0,
          },
    [agentCallMetricsLoadable, agentCallMetricsCached]
  );

  const handleChangeCallTab = (event: any, newValue: any) => {
    setCallValue(newValue);
  };

  const dateRanges = useMemo(() => {
    return getDateRanges(
      agentCallMetricsLoadable?.contents?.response?.data?.charts
    );
  }, [agentCallMetricsLoadable?.contents?.response?.data?.charts]);

  return (
    <>
      <DashboardTourExtraSpotlights />
      <Box>
        <SnapshotDateTabs onSetDates={setDates}>
          <div style={{ textAlign: 'center' }}>
            <SnapshotDateDisplay dateRanges={dateRanges} />
            <SnapshotTitle title={'Call Metrics'} />
            <CallMetrics
              callMetrics={
                agentCallMetricsLoadable?.contents?.response?.data?.charts
              }
              previousRange={dateRanges?.previousRange}
              loading={agentCallMetricsLoadable?.state === 'loading'}
              hasCallTranscriptionProduct={hasCallTranscriptionProduct}
            />
            <AgentCallMetricsBar
              previousRange={dateRanges?.previousRange}
              loading={agentCallMetricsLoadable?.state === 'loading'}
              agentSeries={agentSeries}
              dateInterval={dateInterval}
              userAtomContents={userAtomContents}
              id={+id}
              team={team}
            />
          </div>
        </SnapshotDateTabs>
        <Divider className={classes.divider} />
        <SnapshotTabsBase
          onHandleChange={handleChangeCallTab}
          value={callValue}
          tabs={callReviewTabs}
        >
          <>
            <SnapshotTitle title={'Call Review'} />
            <CallReview
              starting={starting}
              ending={ending}
              callValue={callValue}
              dateInterval={dateInterval}
              hasCallTranscriptionProduct={hasCallTranscriptionProduct}
            />
          </>
        </SnapshotTabsBase>
      </Box>
    </>
  );
}
